import { GET_ACCOUNTS, GET_LOGIN_HISTORY } from "constants/api";

export function sleep(ms, signal) {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(new DOMException("Aborted", "AbortError"));
      return;
    }
    const t = setTimeout(resolve, ms);
    if (signal) {
      const onAbort = () => {
        clearTimeout(t);
        signal.removeEventListener("abort", onAbort);
        reject(new DOMException("Aborted", "AbortError"));
      };
      signal.addEventListener("abort", onAbort, { once: true });
    }
  });
}

export function extractFirstLoginUtcMs(loginHistoryResponse) {
  // Expected shape:
  // [
  //   { history: [ { date: "2026-01-08T19:18:20.7859307Z", ip: "..." }, ... ], ... },
  //   ...
  // ]
  if (!Array.isArray(loginHistoryResponse)) return null;

  let min = null;
  for (const yearly of loginHistoryResponse) {
    const history = yearly?.history;
    if (!Array.isArray(history)) continue;
    for (const entry of history) {
      const ms = Date.parse(entry?.date);
      if (!Number.isFinite(ms)) continue;
      if (min === null || ms < min) min = ms;
    }
  }
  return min;
}

export function normalizeUtcCutoffInputToMs(value) {
  // Accept either:
  // - datetime-local style: "YYYY-MM-DDTHH:mm" or "YYYY-MM-DDTHH:mm:ss"
  //   (interpreted as USER LOCAL TIME, then converted to UTC ms)
  // - ISO UTC: "YYYY-MM-DDTHH:mm:ssZ" (or with fractional seconds)
  if (!value || typeof value !== "string") return null;

  const trimmed = value.trim();
  if (!trimmed) return null;

  // If user already provided a Z/offset, trust Date.parse.
  if (/[zZ]$/.test(trimmed) || /[+-]\d{2}:\d{2}$/.test(trimmed)) {
    const ms = Date.parse(trimmed);
    return Number.isFinite(ms) ? ms : null;
  }

  // Parse datetime-local as local time safely (avoid relying on engine quirks)
  // Supported:
  // - YYYY-MM-DDTHH:mm
  // - YYYY-MM-DDTHH:mm:ss
  const m = trimmed.match(
    /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?$/
  );
  if (!m) return null;

  const year = Number(m[1]);
  const monthIndex = Number(m[2]) - 1; // 0-based
  const day = Number(m[3]);
  const hour = Number(m[4]);
  const minute = Number(m[5]);
  const second = Number(m[6] || "0");

  if (
    !Number.isFinite(year) ||
    !Number.isFinite(monthIndex) ||
    !Number.isFinite(day) ||
    !Number.isFinite(hour) ||
    !Number.isFinite(minute) ||
    !Number.isFinite(second)
  ) {
    return null;
  }

  const localDate = new Date(year, monthIndex, day, hour, minute, second);
  const ms = localDate.getTime(); // UTC epoch ms
  return Number.isFinite(ms) ? ms : null;
}

export async function auditFirstLogins({
  orgId,
  cutoffUtcMs,
  throttleMs = 1000,
  maxBackoffMs = 15000,
  maxRetries429 = 8,
  onProgress,
  signal,
}) {
  if (!orgId) throw new Error("orgId is required");
  if (!Number.isFinite(cutoffUtcMs)) throw new Error("cutoffUtcMs is required");

  const results = {
    auditedCount: 0,
    matchedCount: 0,
    matches: [],
    byUserId: {}, // user_id -> firstLoginUtcMs
    errors: [],
  };

  const emit = (patch) => {
    if (typeof onProgress === "function") onProgress(patch);
  };

  const is429 = (e) => Number(e?.response?.status) === 429;

  // Shared, adaptive delay (increases on 429, relaxes after success)
  let adaptiveDelayMs = throttleMs;

  async function callWith429Backoff(callFn, meta) {
    let attempt = 0;
    // Retry 429 with exponential backoff; otherwise throw.
    // Note: axios errors have `response.status`.
    // We also respect AbortController via sleep(signal) + explicit checks.
    for (;;) {
      if (signal?.aborted) throw new DOMException("Aborted", "AbortError");
      try {
        const res = await callFn();
        // Relax backoff after success (but never below base throttle)
        if (adaptiveDelayMs > throttleMs) {
          adaptiveDelayMs = Math.max(throttleMs, Math.floor(adaptiveDelayMs * 0.8));
        }
        emit({ delayMs: adaptiveDelayMs, last429: false });
        return res;
      } catch (e) {
        if (is429(e) && attempt < maxRetries429) {
          attempt += 1;
          adaptiveDelayMs = Math.min(
            Math.max(adaptiveDelayMs * 2, throttleMs),
            maxBackoffMs
          );
          emit({
            phase: "backoff",
            delayMs: adaptiveDelayMs,
            last429: true,
            backoffAttempt: attempt,
            backoffFor: meta,
          });
          await sleep(adaptiveDelayMs, signal);
          continue;
        }
        throw e;
      }
    }
  }

  // Page 1 fetch to determine total pages
  const firstPageResp = await callWith429Backoff(
    () =>
      GET_ACCOUNTS(`page=1&organization_id=${encodeURIComponent(orgId)}`),
    { kind: "accounts", page: 1 }
  );
  const firstData = firstPageResp?.data || {};
  const count = Number(firstData?.count || 0);
  const pageSize = Number(firstData?.page_size || 0) || 1;
  const totalPages = Math.max(1, Math.ceil(count / pageSize));

  emit({
    phase: "accounts",
    page: 1,
    totalPages,
    totalUsers: count,
    delayMs: adaptiveDelayMs,
  });

  async function processPage(pageNo, pageData) {
    const users = Array.isArray(pageData?.results) ? pageData.results : [];

    for (const user of users) {
      if (signal?.aborted) throw new DOMException("Aborted", "AbortError");

      const userId = user?.id;
      emit({ phase: "user", page: pageNo, userId, delayMs: adaptiveDelayMs });

      try {
        const historyResp = await callWith429Backoff(
          () =>
            GET_LOGIN_HISTORY(`user_id=${encodeURIComponent(userId)}`),
          { kind: "login_history", userId }
        );
        const firstLoginUtcMs = extractFirstLoginUtcMs(historyResp?.data);
        if (Number.isFinite(firstLoginUtcMs)) {
          results.byUserId[userId] = firstLoginUtcMs;
          if (firstLoginUtcMs < cutoffUtcMs) {
            results.matchedCount += 1;
            results.matches.push({
              user,
              firstLoginUtcMs,
            });
            emit({
              phase: "match",
              match: { user, firstLoginUtcMs },
              matchedCount: results.matchedCount,
              auditedCount: results.auditedCount,
              errorsCount: results.errors.length,
              delayMs: adaptiveDelayMs,
            });
          }
        }
      } catch (e) {
        results.errors.push({ userId, error: e?.message || String(e) });
      } finally {
        results.auditedCount += 1;
        emit({
          phase: "progress",
          auditedCount: results.auditedCount,
          matchedCount: results.matchedCount,
          errorsCount: results.errors.length,
          delayMs: adaptiveDelayMs,
        });
      }

      await sleep(adaptiveDelayMs, signal);
    }
  }

  await processPage(1, firstData);
  for (let page = 2; page <= totalPages; page += 1) {
    if (signal?.aborted) throw new DOMException("Aborted", "AbortError");
    emit({ phase: "accounts", page, totalPages, totalUsers: count, delayMs: adaptiveDelayMs });
    const resp = await callWith429Backoff(
      () =>
        GET_ACCOUNTS(`page=${page}&organization_id=${encodeURIComponent(orgId)}`),
      { kind: "accounts", page }
    );
    await processPage(page, resp?.data || {});
  }

  return results;
}


